import { useEffect, useMemo, useState } from 'react';

import { AppointmentType } from 'src/services/availability';
import { UmbracoClinicServiceType } from 'src/services/booking';
import {
    filterToAfterHoursAppointmentTypes,
    filterToServiceType,
    filterToStandardAppointmentTypes,
} from 'src/utils/appointmentTypes';

export function useServiceTypeFilters(
    serviceTypeFilters: UmbracoClinicServiceType[] | undefined,
    appointmentTypes: AppointmentType[],
    defaultTab?: string
) {
    const [serviceType, setServiceType] = useState<string>(() => {
        switch (defaultTab) {
            case 'GENERAL CARE':
            case 'GENERAL':
                return 'General Care';
            case 'AFTER HOURS':
            case 'AFTER-HOURS':
            case 'AFTERHOURS':
                return 'After Hours';
        }
        return 'General Care';
    });

    useEffect(() => {
        for (const serviceTypeFilter of serviceTypeFilters ?? []) {
            if (defaultTab && serviceTypeFilter.label.toLocaleUpperCase().includes(defaultTab)) {
                setServiceType(serviceTypeFilter.label);
            }
        }
    }, [serviceTypeFilters, defaultTab, setServiceType]);

    const specialAppointmentTypes = useMemo(() => {
        const specials: [string, AppointmentType[]][] = [];
        for (const serviceFilter of serviceTypeFilters ?? []) {
            const serviceAppointmentTypes = filterToServiceType(
                appointmentTypes ?? [],
                ...serviceFilter.serviceTypes.map((s) => s.id)
            );
            specials.push([serviceFilter.label, serviceAppointmentTypes]);
        }
        return specials;
    }, [appointmentTypes, serviceTypeFilters]);

    const [standardAppointmentTypes, afterHoursAppointmentTypes] = useMemo(
        () => [
            filterToStandardAppointmentTypes(appointmentTypes)?.filter((a) =>
                specialAppointmentTypes.every(
                    ([_, specialAppointmentTypes]) =>
                        !specialAppointmentTypes.find((sa) => sa.appointmentTypeId === a.appointmentTypeId)
                )
            ),
            filterToAfterHoursAppointmentTypes(appointmentTypes),
        ],
        [appointmentTypes, specialAppointmentTypes]
    );

    const currentSpecialAppointmentType = specialAppointmentTypes.find(([label]) => label === serviceType);

    return {
        serviceType,
        setServiceType,
        standardAppointmentTypes,
        afterHoursAppointmentTypes,
        specialAppointmentTypes,
        currentSpecialAppointmentType,
    };
}
